import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export const PALETTES = [
  { id: 'oceanic', name: 'Oceanic Cactus', swatch: ['#1A535C', '#4ECDC4', '#F7FFF7', '#FF6B6B', '#FFE66D'] },
  { id: 'feel', name: 'Feel Something', swatch: ['#E1A06C', '#C3CED1', '#E6DFDA', '#C29D9E', '#DF829D'] },
  { id: 'butterfield', name: 'BUtterfield 8', swatch: ['#072743', '#11395E', '#12213F', '#066886', '#0291B5'] },
  { id: 'slate', name: 'Slate', swatch: ['#121A26', '#5A6980', '#FFFFFF', '#E09B2D', '#0C7A66'] },
  { id: 'slate-dark', name: 'Slate, after dark', swatch: ['#0C121B', '#1B2533', '#2A3648', '#F0A63A', '#4FC1A6'] },
  // The swatch previews the tokens, not the source palette — text, a mid
  // tone, the screen, then the two colours that carry meaning. A strip of
  // the prettiest five would show a theme you don't get.
  { id: 'autumn', name: 'Autumn Leaves', swatch: ['#46281A', '#A65E2E', '#FDF8F2', '#C65D3B', '#D9A66B'] },
  { id: 'pastels', name: 'Soft Pastels', swatch: ['#45362C', '#C2B280', '#FEFAF7', '#D98D82', '#C98A5A'] },
  { id: 'forest', name: 'Enchanted Forest', swatch: ['#1C3A1E', '#4B8B3B', '#D9E8D2', '#BD6533', '#C98C1E'] },
  { id: 'botanical', name: 'Dark Botanical', swatch: ['#1A131C', '#241B26', '#5E3D62', '#E58FA8', '#85BF92'] },
  { id: 'lilypond', name: 'Lily Pond', swatch: ['#0A3323', '#105666', '#F7F4D5', '#D3968C', '#839958'] },
  { id: 'bubblegum', name: 'Bubblegum', swatch: ['#15484C', '#30B9B2', '#FFFFFF', '#F56483', '#FEA000'] },
  { id: 'lavender', name: 'Lavender Violet', swatch: ['#2A1F42', '#7758A3', '#FFFFFF', '#DB3E8C', '#F6C45C'] },
] as const;

export type PaletteId = (typeof PALETTES)[number]['id'];

const STORAGE_KEY = 'slate-palette';
const DEFAULT: PaletteId = 'oceanic';

function stored(): PaletteId {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && PALETTES.some((p) => p.id === saved)) return saved as PaletteId;
  } catch {
    // Private browsing, or storage disabled. The default is fine.
  }
  return DEFAULT;
}

interface ThemeValue {
  palette: PaletteId;
  setPalette: (id: PaletteId) => void;
}

const ThemeContext = createContext<ThemeValue>({ palette: DEFAULT, setPalette: () => {} });

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [palette, setPaletteState] = useState<PaletteId>(stored);

  useEffect(() => {
    document.documentElement.dataset.palette = palette;
    // Keep the browser chrome in step with the page, or the status bar reads
    // as a seam of the wrong colour above the app. What sits under the status
    // bar is the header, so a palette that wears its colour as a band wants
    // the band here; the rest still want the screen.
    const styles = getComputedStyle(document.documentElement);
    const band = styles.getPropertyValue('--band').trim();
    const top = band && band !== 'transparent' ? band : styles.getPropertyValue('--surface').trim();
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', top);
  }, [palette]);

  const setPalette = (id: PaletteId) => {
    setPaletteState(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // Not being able to remember the choice is not worth an error.
    }
  };

  return <ThemeContext.Provider value={{ palette, setPalette }}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  return useContext(ThemeContext);
}

/**
 * A dropdown rather than a wall of chips: twelve palettes in a wrap took more
 * of Settings than the setting deserves. Closed, it is the current palette on
 * one row; open, every palette gets a row of its own, swatch and all.
 */
export function PalettePicker() {
  const { palette, setPalette } = useTheme();
  const [open, setOpen] = useState(false);
  const current = PALETTES.find((p) => p.id === palette) ?? PALETTES[0];
  return (
    <div className="palette-select">
      {/* Named in full, because the visible text is only half the answer. The
          button reads "Oceanic Cactus" on screen, which works next to the
          section heading and not at all to someone hearing the control on its
          own — "Oceanic Cactus, button, collapsed" says nothing about what it
          changes. The listbox's own label arrives too late: it does not exist
          until the menu is open, which is after the decision to open it. */}
      <button
        type="button"
        className="palette-option current"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Colour theme: ${current.name}`}
        onClick={() => setOpen((o) => !o)}
      >
        <Swatch colours={current.swatch} />
        <span>{current.name}</span>
        <span className="caret" aria-hidden="true">
          {open ? '▴' : '▾'}
        </span>
      </button>
      {open && (
        <div role="listbox" aria-label="Colour theme" className="palette-list">
          {PALETTES.map((p) => (
            <button
              key={p.id}
              type="button"
              role="option"
              aria-selected={palette === p.id}
              className="palette-option"
              onClick={() => {
                setPalette(p.id);
                setOpen(false);
              }}
            >
              <Swatch colours={p.swatch} />
              <span>{p.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Swatch({ colours }: { colours: readonly string[] }) {
  return (
    <span className="sw" aria-hidden="true">
      {colours.map((c) => (
        <i key={c} style={{ background: c }} />
      ))}
    </span>
  );
}
